import { formatWorkWeekId, parseWorkWeekId } from 'src/utils/workWeek';

export const TIME_TAB_DRAFT = 'draft';
export const TIME_TAB_REJECTED = 'rejected';
export const TIME_TAB_MISSING = 'missing';

const FIXED_TABS = [TIME_TAB_DRAFT, TIME_TAB_REJECTED, TIME_TAB_MISSING];

export interface ITimeViewParams {
  /** `draft`, `rejected`, `missing`, or a 1-based workflow step number; null selects no tab. */
  tab: string | null;
  officeId: number | null;
  /** Work-week id (`formatWorkWeekId`); null means the current week. */
  weekId: string | null;
  onlyMyTeam: boolean;
  expandedUserIds: number[];
}

const TAB_PARAM = 'tab';
const OFFICE_PARAM = 'office';
const WEEK_PARAM = 'week';
const MY_TEAM_PARAM = 'myTeam';
const EXPANDED_PARAM = 'expanded';

const STEP_TAB_PATTERN = /^[1-9]\d*$/;
const WEEK_ID_PATTERN = /^\d{4}-\d{1,2}$/;

const parsePositiveInt = (value: string | null): number | null => {
  if (!value || !STEP_TAB_PATTERN.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
};

const readTab = (value: string | null): string | null => {
  if (value === null) return null;
  if (FIXED_TABS.includes(value)) return value;
  return parsePositiveInt(value) === null ? null : value;
};

const readWeekId = (value: string | null): string | null => {
  if (!value || !WEEK_ID_PATTERN.test(value)) return null;
  const { year, weekNumber } = parseWorkWeekId(value);
  if (weekNumber < 1 || weekNumber > 53) return null;
  return formatWorkWeekId(year, weekNumber);
};

const readExpandedUserIds = (value: string | null): number[] => {
  const ids = (value ?? '')
    .split(',')
    .map(parsePositiveInt)
    .filter((id): id is number => id !== null);
  return Array.from(new Set(ids));
};

/** Malformed values read as their defaults rather than failing. */
export const readTimeViewParams = (
  params: URLSearchParams,
): ITimeViewParams => ({
  tab: readTab(params.get(TAB_PARAM)),
  officeId: parsePositiveInt(params.get(OFFICE_PARAM)),
  weekId: readWeekId(params.get(WEEK_PARAM)),
  onlyMyTeam: params.get(MY_TEAM_PARAM) === '1',
  expandedUserIds: readExpandedUserIds(params.get(EXPANDED_PARAM)),
});

/** Default values drop their param; params this page does not own are kept. */
export const writeTimeViewParams = (
  params: URLSearchParams,
  view: ITimeViewParams,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  const setOrDelete = (key: string, value: string | null) => {
    if (value) next.set(key, value);
    else next.delete(key);
  };

  setOrDelete(TAB_PARAM, readTab(view.tab));
  setOrDelete(OFFICE_PARAM, view.officeId ? String(view.officeId) : null);
  setOrDelete(WEEK_PARAM, readWeekId(view.weekId));
  setOrDelete(MY_TEAM_PARAM, view.onlyMyTeam ? '1' : null);
  setOrDelete(
    EXPANDED_PARAM,
    Array.from(new Set(view.expandedUserIds)).join(',') || null,
  );
  return next;
};

/** A step number the office's workflow does not have selects no tab. */
export const resolveTimeTab = (
  tab: string | null,
  stepCount: number,
): string | null => {
  if (tab === null || FIXED_TABS.includes(tab)) return tab;
  const step = parsePositiveInt(tab);
  return step !== null && step <= stepCount ? tab : null;
};
